/**
 * Route Configuration
 * Centralized route definitions for the application
 * @module config/routes
 */

import React from 'react';
import { isTeamLeader, normalizeRole } from '@/utils/roleUtils';
import { canAccessAdminRoute, getRoutePermissionKeys, toAccessUser } from '@/shared/lib/accessControl';

// Lazy load all pages for better code splitting and bundle optimization
// This reduces initial bundle size and improves load time
const DashboardPage = React.lazy(() => import('@/features/dashboard/pages/DashboardPage'));
const PayrollPage = React.lazy(() => import('@/features/payroll/pages/PayrollPage'));
const EmployeesListPage = React.lazy(() => import('@/features/employees/pages/EmployeesListPage'));
const EmployeeDetailsPage = React.lazy(() => import('@/features/employees/pages/EmployeeDetailsPage'));
const SettingsPage = React.lazy(() => import('@/features/settings/pages/SettingsPage'));
const SystemLogsPage = React.lazy(() => import('@/features/settings/pages/SystemLogsPage'));
const AllNotifications = React.lazy(() => import('@/features/dashboard/pages/AllNotifications'));
const ReportsPage = React.lazy(() => import('@/features/reports/pages/ReportsPage'));
const ReportDetailPage = React.lazy(() => import('@/features/reports/pages/ReportDetailPage'));
const TasksPage = React.lazy(() => import('@/features/tasks/pages/TasksPage'));
const AttendancePage = React.lazy(() => import('@/features/attendance/pages/AttendancePage'));
const HolidaysPage = React.lazy(() => import('@/features/attendance/pages/HolidaysPage'));
const PermissionsPage = React.lazy(() => import('@/features/permissions/pages/PermissionsPage'));
const CompanyPage = React.lazy(() => import('@/features/company/pages/CompanyPage'));
const ChatPage = React.lazy(() => import('@/features/chat/pages/ChatPage'));
const LoginPage = React.lazy(() => import('@/features/auth/pages/LoginPage'));
const SignUpPage = React.lazy(() => import('@/features/auth/pages/SignUpPage'));
const ForgetPassword = React.lazy(() => import('@/features/auth/pages/ForgetPassword'));
const LandingPage = React.lazy(() => import('@/features/landing/pages/LandingPage'));
const PrivacyPolicyPage = React.lazy(() => import('@/features/landing/pages/PrivacyPolicyPage'));
const DeleteAccountPage = React.lazy(() => import('@/features/auth/pages/DeleteAccountPage'));
const SupportSessionPage = React.lazy(() => import('@/features/auth/pages/SupportSessionPage'));

// Already lazy loaded
const EmployeesPage = React.lazy(() => import('@/features/employees/pages/EmployeesPage'));
const MePage = React.lazy(() => import('@/features/employees/pages/MePage'));

/**
 * Route configuration object
 * Each route can specify:
 * - path: URL path
 * - component: React component to render
 * - roles: Array of allowed roles (empty = all authenticated users)
 * - requiresAuth: Whether authentication is required
 * - lazy: Whether component should be lazy loaded
 */
export const routeConfig = [
  // Public routes
  {
    path: '/',
    component: LandingPage,
    requiresAuth: false,
    public: true,
    lazy: true
  },
  {
    path: '/login',
    component: LoginPage,
    requiresAuth: false,
    public: true,
    lazy: true
  },
  // {
  //   path: '/signup',
  //   component: SignUpPage,
  //   requiresAuth: false,
  //   public: true
  // },
  {
    path: '/ForgetPassword',
    component: ForgetPassword,
    requiresAuth: false,
    public: true,
    lazy: true
  },
  {
    path: '/privacy',
    component: PrivacyPolicyPage,
    requiresAuth: false,
    public: true,
    lazy: true
  },
  {
    path: '/delete-account',
    component: DeleteAccountPage,
    requiresAuth: false,
    public: true,
    lazy: true
  },
  {
    path: '/support-session',
    component: SupportSessionPage,
    requiresAuth: false,
    public: true,
    lazy: true
  },

  // Dashboard routes - accessible by admin and employee
  {
    path: '/dashboard',
    component: DashboardPage,
    roles: ['admin', 'employee'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/employees',
    component: EmployeesPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/employees/:id',
    component: EmployeeDetailsPage,
    roles: ['admin'],
    requiresAuth: true,
    redirectTo: '/employees/:id/overview',
    lazy: true
  },
  {
    path: '/employees/:id/overview',
    component: EmployeeDetailsPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/employees/:id/documents',
    component: EmployeeDetailsPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/employees/:employeeId/:section',
    component: EmployeeDetailsPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/payroll',
    component: PayrollPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/settings',
    component: SettingsPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/system-logs',
    component: SystemLogsPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/reports',
    component: ReportsPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/reports/:reportId',
    component: ReportDetailPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/company',
    component: CompanyPage,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/notifications',
    component: AllNotifications,
    roles: ['admin'],
    requiresAuth: true,
    lazy: true
  },

  // Shared routes (all authenticated users)
  {
    path: '/tasks',
    component: TasksPage,
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/chat',
    component: ChatPage,
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/attendance',
    component: AttendancePage,
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/holidays',
    component: HolidaysPage,
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/permissions',
    component: PermissionsPage,
    requiresAuth: true,
    lazy: true
  },
  {
    path: '/employees-list',
    component: EmployeesListPage,
    requiresAuth: true,
    lazy: true
  },

  // Employee profile routes (direct paths without /me)
  {
    path: '/salary',
    component: MePage,
    requiresAuth: true,
    props: { activeSection: 'salary' },
    lazy: true
  },
  // Profile routes
  {
    path: '/me',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/dashboard',
    lazy: true
  },
  {
    path: '/me/overview',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/dashboard',
    lazy: true
  },
  {
    path: '/me/desktop-tracking',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/dashboard',
    lazy: true
  },
  {
    path: '/me/salary',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/salary'
  },
  {
    path: '/me/Msg',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/dashboard',
    lazy: true
  },
  {
    path: '/me/attendance',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/dashboard',
    lazy: true
  },
  {
    path: '/me/performance',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/dashboard',
    lazy: true
  },
  {
    path: '/myDocument',
    component: MePage,
    requiresAuth: true,
    props: { activeSection: 'documents' },
    lazy: true
  },
  {
    path: '/me/requests',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/holidays',
    lazy: true
  },
  {
    path: '/me/tasks',
    component: MePage,
    requiresAuth: true,
    redirectTo: '/tasks',
    lazy: true
  },
];

/**
 * Get default route for a role
 * @param {string} role - User role
 * @returns {string} - Default route path
 */
export const getDefaultRoute = (role) => {
  const normalizedRole = normalizeRole(role);
  const roleRoutes = {
    admin: '/dashboard',
    employee: '/dashboard',
    teamLeader: '/dashboard',
  };
  return roleRoutes[normalizedRole] || '/dashboard';
};

/**
 * Check if a route requires authentication
 * @param {string} path - Route path
 * @returns {boolean} - Whether authentication is required
 */
export const isRoutePublic = (path) => {
  const route = routeConfig.find(r => r.path === path);
  return route?.public === true;
};

/**
 * Check if user has access to a route
 * @param {string} path - Route path
 * @param {string|Object} userOrRole - User object or role string
 * @returns {boolean} - Whether user has access
 */
export const hasRouteAccess = (path, userOrRole) => {
  const route = routeConfig.find(r => r.path === path || path.startsWith(r.path + '/'));
  if (!route) return false;
  if (route.public) return true;
  if (!route.requiresAuth) return true;

  const user = toAccessUser(userOrRole);
  const normalizedRole = normalizeRole(user.role);

  if (!route.roles || route.roles.length === 0) {
    if (isTeamLeader(normalizedRole)) {
      const keys = getRoutePermissionKeys(path);
      if (keys !== undefined) return canAccessAdminRoute(user, keys);
    }
    return true;
  }
  if (route.roles.includes(normalizedRole)) return true;
  if (isTeamLeader(normalizedRole) && route.roles.includes('employee')) return true;
  if (isTeamLeader(normalizedRole) && route.roles.includes('admin')) {
    return canAccessAdminRoute(user, getRoutePermissionKeys(path));
  }
  return false;
};

